import React, { useRef, useCallback, useId, memo, useMemo, useState } from 'react';
import { Text } from '../../atoms/Text';
import { Flex } from '../../atoms/Flex';
import { Select } from '../../atoms/Select';
import { Spinner } from '../../atoms/Spinner';
import { Pagination } from '../Pagination';
import './Table.css';

export interface Column<T> {
  /**
   * Key to access the data field (can be a path like 'user.name')
   */
  key: keyof T | string;
  /**
   * Header label text
   */
  label: string;
  /**
   * Whether this column is sortable
   */
  sortable?: boolean;
  /**
   * CSS width value (e.g., 'auto', '150px', '20%')
   */
  width?: string;
  /**
   * Text alignment
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Custom render function for cell content
   */
  render?: (value: unknown, row: T, rowIndex: number) => React.ReactNode;
  /**
   * Hide this column on mobile (shows in card instead)
   */
  hideOnMobile?: boolean;
}

export interface PaginationConfig {
  /**
   * Current page number (1-indexed)
   */
  currentPage: number;
  /**
   * Number of items per page
   */
  pageSize: number;
  /**
   * Total number of items across all pages
   */
  totalItems: number;
  /**
   * Available page size options
   */
  pageSizeOptions?: number[];
}

export interface TableProps<T> {
  /**
   * Column definitions
   */
  columns: Column<T>[];
  /**
   * Data rows to display
   */
  data: T[];
  /**
   * Whether data is currently loading
   */
  loading?: boolean;
  /**
   * Message to show while loading
   */
  loadingMessage?: string;
  /**
   * Message to show when data is empty
   */
  emptyMessage?: string;
  /**
   * Callback when a sortable column header is clicked
   */
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  /**
   * Currently sorted column key
   */
  sortKey?: string;
  /**
   * Current sort direction
   */
  sortDirection?: 'asc' | 'desc';
  /**
   * Unique key extractor for rows (defaults to index)
   */
  getRowKey?: (row: T, index: number) => string | number;
  /**
   * Accessible label for the table (used by screen readers)
   */
  ariaLabel?: string;
  /**
   * Optional CSS class name
   */
  className?: string;
  /**
   * Optional inline styles
   */
  style?: React.CSSProperties;
  /**
   * Pagination configuration (enables pagination when provided)
   */
  pagination?: PaginationConfig;
  /**
   * Callback when page changes
   */
  onPageChange?: (page: number) => void;
  /**
   * Callback when page size changes
   */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Enable sticky header with a max height for the scrollable container.
   * When provided, the header row stays visible while scrolling.
   * Examples: '400px', '50vh', '100%'
   */
  stickyMaxHeight?: string;
  /**
   * Callback when a row is clicked.
   * When provided, rows become clickable with cursor pointer styling.
   */
  onRowClick?: (row: T, rowIndex: number) => void;
  /**
   * Enable row selection with checkboxes.
   * When true, a checkbox column is added as the first column.
   */
  selectable?: boolean;
  /**
   * Set of selected row keys (controlled).
   * Use with onSelectionChange for controlled selection.
   */
  selectedRows?: Set<string | number>;
  /**
   * Callback when selection changes.
   * Receives the new Set of selected row keys.
   */
  onSelectionChange?: (selectedRows: Set<string | number>) => void;
}

/**
 * Get a nested value from an object using a dot-notation path
 */
const getNestedValue = <T,>(obj: T, path: string): unknown => {
  return path.split('.').reduce((acc: unknown, part: string) => {
    if (acc && typeof acc === 'object' && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
};

/**
 * Extract row key from row data using custom function or common id fields
 */
const extractRowKey = <T extends object>(
  row: T,
  index: number,
  getRowKey?: (row: T, index: number) => string | number
): string | number => {
  if (getRowKey) {
    return getRowKey(row, index);
  }
  const rowObj = row as Record<string, unknown>;
  if ('id' in row) return String(rowObj.id);
  if ('uuid' in row) return String(rowObj.uuid);
  if ('key' in row) return String(rowObj.key);
  return index;
};

/**
 * Get cell value with optional custom renderer
 */
const getCellValue = <T,>(row: T, column: Column<T>, rowIndex: number): React.ReactNode => {
  const value = getNestedValue(row, String(column.key));

  if (column.render) {
    return column.render(value, row, rowIndex);
  }

  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

// ============================================================================
// Sub-components
// ============================================================================

interface SortIconProps {
  sortable?: boolean;
  isActive: boolean;
  direction?: 'asc' | 'desc';
}

const SortIcon: React.FC<SortIconProps> = ({ sortable, isActive, direction }) => {
  if (!sortable) return null;

  return (
    <span className="hi-ui-table-sort-icon">
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={`hi-ui-table-sort-arrow hi-ui-table-sort-arrow-up ${isActive && direction === 'asc' ? 'hi-ui-table-sort-active' : ''}`}
      >
        <path d="M18 15l-6-6-6 6" />
      </svg>
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={`hi-ui-table-sort-arrow hi-ui-table-sort-arrow-down ${isActive && direction === 'desc' ? 'hi-ui-table-sort-active' : ''}`}
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </span>
  );
};

interface TableHeaderProps<T> {
  columns: Column<T>[];
  stickyHeader: boolean;
  isSelectable: boolean;
  allSelected: boolean;
  indeterminate: boolean;
  uniqueId: string;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSelectAll: () => void;
  onSort: (column: Column<T>) => void;
}

function TableHeaderComponent<T>({
  columns,
  stickyHeader,
  isSelectable,
  allSelected,
  indeterminate,
  uniqueId,
  sortKey,
  sortDirection,
  onSelectAll,
  onSort,
}: TableHeaderProps<T>): React.ReactElement {
  return (
    <thead className={stickyHeader ? 'hi-ui-table-thead-sticky' : ''}>
      <tr className="hi-ui-table-header">
        {isSelectable && (
          <th className="hi-ui-table-header-cell hi-ui-table-select-cell" scope="col">
            <input
              type="checkbox"
              className="hi-ui-table-checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = indeterminate;
              }}
              onChange={onSelectAll}
              aria-label="Select all rows"
              id={`${uniqueId}-select-all`}
            />
          </th>
        )}
        {columns.map((column) => {
          const key = String(column.key);
          const isActive = sortKey === key;

          return (
            <th
              key={key}
              className={`hi-ui-table-header-cell ${column.sortable ? 'hi-ui-table-header-sortable' : ''} ${column.hideOnMobile ? 'hi-ui-table-hide-mobile' : ''}`}
              style={{ textAlign: column.align || 'left', width: column.width }}
              onClick={() => onSort(column)}
              onKeyDown={(e) => {
                if (column.sortable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSort(column);
                }
              }}
              tabIndex={column.sortable ? 0 : undefined}
              aria-sort={
                isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined
              }
              scope="col"
            >
              <span className="hi-ui-table-header-content">
                <span className="hi-ui-table-header-label">{column.label}</span>
                <SortIcon
                  sortable={column.sortable}
                  isActive={isActive}
                  direction={isActive ? sortDirection : undefined}
                />
              </span>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

const TableHeader = memo(TableHeaderComponent) as typeof TableHeaderComponent;

interface TableRowProps<T> {
  row: T;
  rowIndex: number;
  rowKey: string | number;
  columns: Column<T>[];
  isSelectable: boolean;
  isSelected: boolean;
  uniqueId: string;
  onSelectRow: (rowKey: string | number) => void;
  onRowClick?: (row: T, rowIndex: number) => void;
}

function TableRowComponent<T>({
  row,
  rowIndex,
  rowKey,
  columns,
  isSelectable,
  isSelected,
  uniqueId,
  onSelectRow,
  onRowClick,
}: TableRowProps<T>): React.ReactElement {
  const clickable = !!onRowClick;
  return (
    <tr
      className={`hi-ui-table-row ${isSelected ? 'hi-ui-table-row-selected' : ''} ${clickable ? 'hi-ui-table-row-clickable' : ''}`}
      aria-selected={isSelectable ? isSelected : undefined}
      onClick={clickable ? () => onRowClick(row, rowIndex) : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}
    >
      {isSelectable && (
        <td className="hi-ui-table-cell hi-ui-table-select-cell">
          <input
            type="checkbox"
            className="hi-ui-table-checkbox"
            checked={isSelected}
            onChange={() => onSelectRow(rowKey)}
            aria-label={`Select row ${rowIndex + 1}`}
            id={`${uniqueId}-row-${rowKey}`}
          />
        </td>
      )}
      {columns.map((column) => (
        <td
          key={String(column.key)}
          className={`hi-ui-table-cell ${column.hideOnMobile ? 'hi-ui-table-hide-mobile' : ''}`}
          style={{ textAlign: column.align || 'left' }}
          data-label={column.label}
        >
          {getCellValue(row, column, rowIndex)}
        </td>
      ))}
    </tr>
  );
}

const TableRow = memo(TableRowComponent) as typeof TableRowComponent;

interface MobileCardProps<T> {
  row: T;
  rowIndex: number;
  rowKey: string | number;
  columns: Column<T>[];
  isSelectable: boolean;
  isSelected: boolean;
  uniqueId: string;
  onSelectRow: (rowKey: string | number) => void;
  onRowClick?: (row: T, rowIndex: number) => void;
}

function MobileCardComponent<T>({
  row,
  rowIndex,
  rowKey,
  columns,
  isSelectable,
  isSelected,
  uniqueId,
  onSelectRow,
  onRowClick,
}: MobileCardProps<T>): React.ReactElement {
  const clickable = !!onRowClick;
  return (
    <article
      className={`hi-ui-table-card ${isSelected ? 'hi-ui-table-card-selected' : ''} ${clickable ? 'hi-ui-table-card-clickable' : ''}`}
      role="listitem"
      aria-selected={isSelectable ? isSelected : undefined}
      onClick={clickable ? () => onRowClick(row, rowIndex) : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}
    >
      {isSelectable && (
        <div className="hi-ui-table-card-select">
          <input
            type="checkbox"
            className="hi-ui-table-checkbox"
            checked={isSelected}
            onChange={() => onSelectRow(rowKey)}
            aria-label={`Select row ${rowIndex + 1}`}
            id={`${uniqueId}-card-${rowKey}`}
          />
          <label htmlFor={`${uniqueId}-card-${rowKey}`} className="hi-ui-table-card-select-label">
            Select
          </label>
        </div>
      )}
      {columns.map((column) => (
        <div key={String(column.key)} className="hi-ui-table-card-row">
          <span className="hi-ui-table-card-label">{column.label}</span>
          <span className="hi-ui-table-card-value">{getCellValue(row, column, rowIndex)}</span>
        </div>
      ))}
    </article>
  );
}

const MobileCard = memo(MobileCardComponent) as typeof MobileCardComponent;

interface MobileSortBarProps<T> {
  columns: Column<T>[];
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
}

function MobileSortBarComponent<T>({
  columns,
  sortKey,
  sortDirection = 'asc',
  onSort,
}: MobileSortBarProps<T>): React.ReactElement | null {
  const sortableColumns = columns.filter((c) => c.sortable);
  if (sortableColumns.length === 0 || !onSort) return null;

  const activeSortKey = sortKey || String(sortableColumns[0].key);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const key = e.target.value;
    onSort(key, key === activeSortKey ? sortDirection : 'asc');
  };

  const toggleDirection = () => {
    onSort(activeSortKey, sortDirection === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="hi-ui-table-mobile-sort">
      <label className="hi-ui-table-mobile-sort-label" htmlFor="hi-ui-table-mobile-sort-select">
        Sort by
      </label>
      <Select
        id="hi-ui-table-mobile-sort-select"
        className="hi-ui-table-mobile-sort-select"
        size="sm"
        value={activeSortKey}
        onChange={handleChange}
        aria-label="Sort by column"
      >
        {sortableColumns.map((col) => (
          <option key={String(col.key)} value={String(col.key)}>
            {col.label}
          </option>
        ))}
      </Select>
      <button
        className="hi-ui-table-mobile-sort-direction"
        onClick={toggleDirection}
        aria-label={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
        type="button"
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          {sortDirection === 'asc' ? (
            <path d="M12 19V5M5 12l7-7 7 7" />
          ) : (
            <path d="M12 5v14M19 12l-7 7-7-7" />
          )}
        </svg>
      </button>
    </div>
  );
}

const MobileSortBar = memo(MobileSortBarComponent) as typeof MobileSortBarComponent;

// ============================================================================
// Main Table Component
// ============================================================================

export function Table<T extends object>({
  columns,
  data,
  loading = false,
  loadingMessage = 'Loading...',
  emptyMessage = 'No data available',
  onSort,
  sortKey,
  sortDirection,
  getRowKey,
  ariaLabel,
  className = '',
  style,
  pagination,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  stickyMaxHeight,
  selectable = false,
  selectedRows,
  onSelectionChange,
}: TableProps<T>): React.ReactElement {
  const stickyHeader = !!stickyMaxHeight;
  const uniqueId = useId();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sortAnnouncement, setSortAnnouncement] = useState<string>('');

  // Selection helpers
  const isSelectable = selectable && onSelectionChange;
  const selected = useMemo(() => selectedRows ?? new Set<string | number>(), [selectedRows]);

  // Compute row keys for selection logic (memoized)
  const rowKeys = useMemo(
    () => data.map((row, index) => extractRowKey(row, index, getRowKey)),
    [data, getRowKey]
  );

  // Selection state derived values (memoized)
  const { allSelected, indeterminate } = useMemo(() => {
    const all = data.length > 0 && rowKeys.every((key) => selected.has(key));
    const some = rowKeys.some((key) => selected.has(key));
    return { allSelected: all, indeterminate: some && !all };
  }, [data.length, rowKeys, selected]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      // Deselect all rows on current page
      const newSelection = new Set(selected);
      rowKeys.forEach((key) => newSelection.delete(key));
      onSelectionChange!(newSelection);
    } else {
      // Select all rows on current page
      const newSelection = new Set(selected);
      rowKeys.forEach((key) => newSelection.add(key));
      onSelectionChange!(newSelection);
    }
  }, [onSelectionChange, allSelected, rowKeys, selected]);

  const handleSelectRow = useCallback(
    (rowKey: string | number) => {
      const newSelection = new Set(selected);
      if (newSelection.has(rowKey)) {
        newSelection.delete(rowKey);
      } else {
        newSelection.add(rowKey);
      }
      onSelectionChange!(newSelection);
    },
    [onSelectionChange, selected]
  );

  const handleSort = useCallback(
    (column: Column<T>) => {
      if (!column.sortable || !onSort) return;
      const key = String(column.key);
      const newDirection: 'asc' | 'desc' =
        sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(key, newDirection);
      // Announce sort change to screen readers
      const directionText = newDirection === 'asc' ? 'ascending' : 'descending';
      setSortAnnouncement(`Sorted by ${column.label}, ${directionText}`);
    },
    [onSort, sortKey, sortDirection]
  );

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current!;
    if (container.scrollTop > 0) {
      container.classList.add('hi-ui-table-scrolled');
    } else {
      container.classList.remove('hi-ui-table-scrolled');
    }
  }, []);

  const tableContainerStyle: React.CSSProperties | undefined = stickyMaxHeight
    ? { maxHeight: stickyMaxHeight }
    : undefined;

  const colSpan = useMemo(
    () => columns.length + (isSelectable ? 1 : 0),
    [columns.length, isSelectable]
  );

  const desktopTable = (
    <table className="hi-ui-table-desktop" aria-label={ariaLabel}>
      <TableHeader
        columns={columns}
        stickyHeader={stickyHeader}
        isSelectable={!!isSelectable}
        allSelected={allSelected}
        indeterminate={indeterminate}
        uniqueId={uniqueId}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
      />
      <tbody className="hi-ui-table-body">
        {loading ? (
          <tr className="hi-ui-table-loading">
            <td className="hi-ui-table-loading-cell" colSpan={colSpan}>
              <Flex direction="row" align="center" gap="md" role="status" aria-live="polite">
                <Spinner size="small" />
                <Text inline>{loadingMessage}</Text>
              </Flex>
            </td>
          </tr>
        ) : data.length === 0 ? (
          <tr className="hi-ui-table-empty">
            <td
              className="hi-ui-table-empty-cell"
              colSpan={colSpan}
              role="status"
              aria-live="polite"
            >
              <Text>{emptyMessage}</Text>
            </td>
          </tr>
        ) : (
          data.map((row, rowIndex) => {
            const rowKey = extractRowKey(row, rowIndex, getRowKey);
            return (
              <TableRow
                key={rowKey}
                row={row}
                rowIndex={rowIndex}
                rowKey={rowKey}
                columns={columns}
                isSelectable={!!isSelectable}
                isSelected={selected.has(rowKey)}
                uniqueId={uniqueId}
                onSelectRow={handleSelectRow}
                onRowClick={onRowClick}
              />
            );
          })
        )}
      </tbody>
    </table>
  );

  return (
    <div className={`hi-ui hi-ui-table ${className}`.trim()} style={style}>
      {/* Screen reader announcements for sort changes */}
      <div aria-live="polite" aria-atomic="true" className="hi-ui-sr-only">
        {sortAnnouncement}
      </div>

      {/* Desktop Table View */}
      {stickyHeader ? (
        <div
          ref={scrollContainerRef}
          className="hi-ui-table-scroll-container"
          style={tableContainerStyle}
          onScroll={handleScroll}
        >
          {desktopTable}
        </div>
      ) : (
        desktopTable
      )}

      {/* Mobile Card View */}
      <div className="hi-ui-table-mobile">
        <MobileSortBar
          columns={columns}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <div role="list" aria-label={ariaLabel}>
          {loading ? (
            <div className="hi-ui-table-loading-card" role="status" aria-live="polite">
              <Flex direction="row" align="center" gap="sm">
                <Spinner size="small" />
                <Text>{loadingMessage}</Text>
              </Flex>
            </div>
          ) : data.length === 0 ? (
            <div className="hi-ui-table-empty-card" role="status" aria-live="polite">
              <Text>{emptyMessage}</Text>
            </div>
          ) : (
            data.map((row, rowIndex) => {
              const rowKey = extractRowKey(row, rowIndex, getRowKey);
              return (
                <MobileCard
                  key={rowKey}
                  row={row}
                  rowIndex={rowIndex}
                  rowKey={rowKey}
                  columns={columns}
                  isSelectable={!!isSelectable}
                  isSelected={selected.has(rowKey)}
                  uniqueId={uniqueId}
                  onSelectRow={handleSelectRow}
                  onRowClick={onRowClick}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination && onPageChange && (
        <Pagination
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
          pageSizeOptions={pagination.pageSizeOptions}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          aria-label={ariaLabel ? `${ariaLabel} pagination` : 'Table pagination'}
        />
      )}
    </div>
  );
}
